/** Text-like editors: text, long text, URL, dice, number, real date. Commit on blur or Enter. */
import { TextField } from '@codex/ui';
import { type KeyboardEvent, useId } from 'react';
import { propText } from '../../model/values.js';
import type { EditorProps } from './shared.js';

export interface TextEditorProps extends EditorProps {
  /** The unsaved text, when it differs from the stored one. */
  readonly draft: string | undefined;
  readonly setDraft: (text: string | undefined) => void;
}

const INPUT_TYPE: Readonly<Record<string, string>> = { real_date: 'date', url: 'text' };

export function TextEditor({ field, value, error, commit, draft, setDraft }: TextEditorProps) {
  const stored = propText(value);
  const text = draft ?? stored;
  const send = () => {
    if (draft !== undefined && draft !== stored) {
      if (error === null) commit(draft);
    } else setDraft(undefined);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };
  return (
    <TextField
      label={field.label}
      type={INPUT_TYPE[field.kind] ?? 'text'}
      value={text}
      data-field-id={field.id}
      inputMode={field.kind === 'number' ? 'decimal' : undefined}
      aria-invalid={error !== null ? true : undefined}
      description={error ?? undefined}
      onChange={(event) => setDraft(event.currentTarget.value)}
      onBlur={send}
      onKeyDown={onKeyDown}
    />
  );
}

export function LongTextEditor({ field, value, error, commit, draft, setDraft }: TextEditorProps) {
  const id = useId();
  const stored = propText(value);
  const text = draft ?? stored;
  const send = () => {
    if (draft !== undefined && draft !== stored) {
      if (error === null) commit(draft);
    } else setDraft(undefined);
  };
  return (
    <div className="cx-field">
      <label htmlFor={id} className="cx-field__label">
        {field.label}
      </label>
      <span className="cx-input" data-control-boundary="">
        <textarea
          id={id}
          className="cx-input__el"
          rows={4}
          value={text}
          data-field-id={field.id}
          aria-invalid={error !== null ? true : undefined}
          aria-describedby={error !== null ? `${id}-error` : undefined}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onBlur={send}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              send();
            }
          }}
        />
      </span>
      {error !== null && (
        <span id={`${id}-error`} className="cx-field__description">
          {error}
        </span>
      )}
    </div>
  );
}
